import * as React from 'react';
import { FC } from 'react';

import { HStack, Text, VStack } from '@rbilabs/universal-components';
import { capitalize } from 'lodash-es';
import { useIntl } from 'react-intl';

import Currency from 'components/currency';
import { usePrevious } from 'hooks/use-previous';
import { selectors, useAppSelector } from 'state/global-state';
import { useUIContext } from 'state/ui';
import { primitive } from 'styles/constants/primitives';
import { getItemLoyaltyRewardDiscount } from 'utils/cart/helper';
import priceForCartEntry from 'utils/menu/price-for-cart-entry';

import { IPrice } from '../types';

const commonTextConfig = {
  fontWeight: 'bold',
  fontSize: 'sm',
  lineHeight: 'sm',
  margin: 0,
  color: primitive.bk.$bbqBrown,
};

const RewardsFreeText = Text.withConfig({
  ...commonTextConfig,
});

const PriceFree = () => {
  const { formatMessage } = useIntl();

  const rewardsFreeText = capitalize(formatMessage({ id: 'free' }));
  return (
    <HStack alignItems="center" focusable accessibilityLabel={rewardsFreeText}>
      <RewardsFreeText>{rewardsFreeText}</RewardsFreeText>
    </HStack>
  );
};

export const Price: FC<React.PropsWithChildren<IPrice>> = ({ item, isOffer, rewardApplied }) => {
  const price = priceForCartEntry(item);
  const { formatMessage } = useIntl();
  const { formatCurrencyForLocale } = useUIContext();
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const isRewardApplied = Boolean(rewardApplied && !isOffer);
  const discountPrice =
    price - getItemLoyaltyRewardDiscount({ cartEntry: item, appliedLoyaltyRewards });
  const prevDiscountPrice = usePrevious(discountPrice);
  const discountDisplay = rewardApplied ? discountPrice : prevDiscountPrice;

  const beforeDiscountPriceLabel = `${formatCurrencyForLocale(price)} ${
    rewardApplied ? formatMessage({ id: 'beforeDiscount' }) : ''
  }`;

  const afterDiscountLabel = `${discountDisplay} ${formatMessage({ id: 'afterDiscount' })}`;

  return (
    <VStack alignItems="flex-end" marginLeft="$2">
      {rewardApplied && discountPrice === 0 ? (
        <PriceFree />
      ) : (
        <Currency
          amount={isRewardApplied ? discountDisplay : price}
          textProps={{
            accessibilityLabel: discountDisplay ? afterDiscountLabel : beforeDiscountPriceLabel,
          }}
          isBold
        />
      )}
    </VStack>
  );
};
